import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import { FunctionComponent, useMemo, useState } from "react";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../ui/pages/content_panel/ui/new_components/common/loading_spinner";
import StadiumButton from "../../../../ui/new_components/common/stadium_button";
import { PurchaseOrderModel } from "../../model/purchase_order_model";
import { currencyFormat } from "../../../../utils/currency_format";
import { urls } from "../../../../utils/urls";
import { fetchGet, toQueryString } from "../../../../service/api_client";

interface ExportPOProps {
  filter: {
    page: number;
    count: number;
    all: boolean | null;
    search?: string;
    fromDate?: Date;
    toDate?: Date;
    party_id?: string;
    party_name?: string;
    approval_status?: string;
  };
}

type ExportDateRangePreset = "last_2_months" | "last_3_months";

const ExportPreviousPO: FunctionComponent<ExportPOProps> = (props) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState<ExportDateRangePreset>("last_2_months");
  const [fromDate, setFromDate] = useState<Date | undefined>(
    props.filter?.fromDate ? new Date(props.filter.fromDate) : undefined,
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    props.filter?.toDate ? new Date(props.filter.toDate) : undefined,
  );
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/prev-purchase-order`;

  const selectedDateRange = useMemo(() => {
    // If user has explicitly selected dates, use those.
    if (fromDate && toDate) {
      return { fromDate, toDate, source: "custom" as const };
    }

    // Otherwise fall back to preset.
    const now = new Date();
    const presetTo = new Date(new Date(now).setHours(23, 59, 59, 999));
    const from = new Date(presetTo);
    if (preset === "last_3_months") {
      from.setMonth(from.getMonth() - 3);
    } else {
      from.setMonth(from.getMonth() - 2);
    }
    const presetFrom = new Date(new Date(from).setHours(0, 0, 0, 0));
    return {
      fromDate: presetFrom,
      toDate: presetTo,
      source: "preset" as const,
    };
  }, [fromDate, toDate, preset]);

  const rangeDays = useMemo(() => {
    const ms =
      selectedDateRange.toDate.getTime() - selectedDateRange.fromDate.getTime();
    return Math.ceil(ms / (1000 * 60 * 60 * 24)) + 1;
  }, [selectedDateRange]);

  const applyPreset = (p: ExportDateRangePreset) => {
    setPreset(p);
    setFromDate(undefined);
    setToDate(undefined);
  };

  const getPOSummarisedListData = async () => {
    const filter = props.filter;
    setLoading(true);
    try {
      const MAX_DAYS = 93; // roughly 3 months; prevents "content too large"
      if (rangeDays > MAX_DAYS) {
        throw new Error(
          `Selected date range is too large (${rangeDays} days). Please select ${MAX_DAYS} days or less.`,
        );
      }

      const searchProp = { ...filter };
      searchProp.all = true;
      searchProp.fromDate = selectedDateRange.fromDate;
      searchProp.toDate = selectedDateRange.toDate;
      const res = await fetchGet(url + toQueryString(searchProp));
      if (res.success) {
        console.log(res.data.data);
        if (res.data.data.length === 0) {
          throw new Error("No data found");
        }
        if (res.data) {
          const options = {
            filename: `PO Report`,
            fieldSeparator: ",",
            quoteStrings: '"',
            decimalSeparator: ".",
            showLabels: true,
            showTitle: false,
            title: "PO Report",
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true,
          };

          const csvExporter = new ExportToCsv(options);

          csvExporter.generateCsv(
            res.data.data.map((data: PurchaseOrderModel, i: number) => {
              return {
                "PO Number": data.po_no,
                Project: data?.project ?? "--",
                "PO Date": moment(data.created_at, moment.ISO_8601).format(
                  "DD-MM-YYYY",
                ),
                Item:
                  data.items.length > 0
                    ? `=HYPERLINK("https://${window.location.host}${
                        urls.purchase_order_prev + "/update/" + data.id
                      }", "${data.items[0].item.name}${
                        data.items.length > 1
                          ? `... and ${data.items.length - 1} more`
                          : ""
                      }")`
                    : "--",
                "Party Name": data.party?.name ?? "--",
                "Responsible Person": data.shipping_profile.contact_name,
                Warehouse: data.warehouse?.name ?? "--",
                "Approval Status":
                  data.approval_level === data.current_approval_level &&
                  (data.current_approval_count ?? 0) >=
                    (data.current_approval_required_count ?? 0)
                    ? "Approved"
                    : (data.current_approval_required_count ?? 0) > 0 &&
                        (data.current_reject_count ?? 0) -
                          (data.current_approval_count ?? 0) >=
                          (data.current_approval_required_count ?? 0)
                      ? "Rejected"
                      : "Pending",
                Total: currencyFormat(
                  data.total,
                  data?.vendor_currency?.short_name,
                ),
                Status: data.status,
                Attachment: data.attachments.length,
              };
            }),
          );
        }
      } else {
        console.log(res.error);
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  return (
    <div className="p-5">
      <div className="flex flex-col items-center gap-2">
        <div className="text-sm text-gray-600">
          Export range:{" "}
          <span className="font-semibold">
            {moment(selectedDateRange.fromDate).format("DD-MMM-YYYY")}
          </span>{" "}
          to{" "}
          <span className="font-semibold">
            {moment(selectedDateRange.toDate).format("DD-MMM-YYYY")}
          </span>
          <span className="ml-2 text-xs text-gray-500">({rangeDays} days)</span>
        </div>
        <div className="flex gap-2 flex-wrap justify-center items-center">
          <button
            type="button"
            onClick={() => applyPreset("last_2_months")}
            className={`rounded-lg border px-3 py-1 text-sm ${
              preset === "last_2_months" &&
              selectedDateRange.source === "preset"
                ? "bg-gray-800 text-white"
                : "hover:bg-gray-100"
            }`}
          >
            Last 2 months
          </button>
          <button
            type="button"
            onClick={() => applyPreset("last_3_months")}
            className={`rounded-lg border px-3 py-1 text-sm ${
              preset === "last_3_months" &&
              selectedDateRange.source === "preset"
                ? "bg-gray-800 text-white"
                : "hover:bg-gray-100"
            }`}
          >
            Last 3 months
          </button>
        </div>

        <div className="flex gap-3 flex-wrap justify-center items-end mt-2">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500" htmlFor="export_from">
              From
            </label>
            <input
              id="export_from"
              type="date"
              className="p-1 rounded border focus:outline-none"
              value={fromDate ? moment(fromDate).format("YYYY-MM-DD") : ""}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setFromDate(undefined);
                  return;
                }
                const d = new Date(v);
                setFromDate(new Date(new Date(d).setHours(0, 0, 0, 0)));
              }}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500" htmlFor="export_to">
              To
            </label>
            <input
              id="export_to"
              type="date"
              className="p-1 rounded border focus:outline-none"
              value={toDate ? moment(toDate).format("YYYY-MM-DD") : ""}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setToDate(undefined);
                  return;
                }
                const d = new Date(v);
                setToDate(new Date(new Date(d).setHours(23, 59, 59, 999)));
              }}
            />
          </div>

          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => {
                setFromDate(undefined);
                setToDate(undefined);
              }}
              className="rounded-lg border px-3 py-1 text-sm hover:bg-gray-100"
              title="Clear custom dates"
            >
              Clear dates
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-4 justify-center my-4">
        <StadiumButton type="button" onTap={getPOSummarisedListData}>
          Summarised Export
        </StadiumButton>
      </div>

      <LoadingWidget loading={loading} />
    </div>
  );
};

export default ExportPreviousPO;
